/** globalParams + seyrek lineOverrides, imza ve preset indirgeme/genişletme. */

import {
  clampOccupancy,
  OCCUPANCY_DEFAULT,
} from "./frequencyScenarios.js";

export const DECISION_STORAGE_KEY = "owsuite.freqopt.decision.v1";

export const GLOBAL_ONLY_KEYS = [
  "gun",
  "basitMaliyet",
  "seferMaliyet",
  "aracMaliyet",
  "kiyas_kaynagi",
  "kiyas_senaryo_id",
];

/** Hat bazında override edilebilir alanlar (gün ve klasik maliyet global). */
export const OVERRIDE_KEYS = [
  "doluluk",
  "kapasite",
  "minFrekans",
  "typePolicy",
  "vehicleTypeCode",
  "allowedTypeCodes",
  "mevcutAracTipi",
  "otomatik",
  "tripFlexEnabled",
  "maxFlexMinutes",
  "flexMode",
];

export const PARAM_KEYS = [...GLOBAL_ONLY_KEYS, ...OVERRIDE_KEYS];

export function createDefaultGlobalParams(overrides = {}) {
  return normalizeParams({
    gun: "HAFTAICI",
    doluluk: OCCUPANCY_DEFAULT,
    kapasite: 90,
    minFrekans: 2,
    typePolicy: "auto",
    vehicleTypeCode: "SOLO",
    allowedTypeCodes: [],
    mevcutAracTipi: "",
    otomatik: true,
    tripFlexEnabled: true,
    maxFlexMinutes: 3,
    flexMode: "AUTO",
    basitMaliyet: false,
    seferMaliyet: 8,
    aracMaliyet: 800,
    kiyas_kaynagi: "baz",
    kiyas_senaryo_id: null,
    ...overrides,
  });
}

function asArray(value) {
  if (Array.isArray(value)) return value.map((x) => String(x)).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
}

export function normalizeParams(raw = {}) {
  const typePolicy = String(
    raw.typePolicy ?? raw.type_policy ?? (raw.basitMaliyet ? "classic" : "auto")
  )
    .trim()
    .toLowerCase() || "auto";
  const basit = Boolean(raw.basitMaliyet) || typePolicy === "classic";
  const codes = asArray(raw.allowedTypeCodes ?? raw.allowed_type_codes);
  codes.sort();
  return {
    gun: String(raw.gun || "HAFTAICI").toUpperCase(),
    doluluk: clampOccupancy(raw.doluluk, OCCUPANCY_DEFAULT),
    kapasite: Number.isFinite(Number(raw.kapasite)) ? Number(raw.kapasite) : 90,
    minFrekans: Number.isFinite(Number(raw.minFrekans)) ? Number(raw.minFrekans) : 2,
    typePolicy: basit ? "classic" : typePolicy,
    vehicleTypeCode: String(
      raw.vehicleTypeCode ?? raw.vehicle_type_code ?? raw.aracTipi ?? "SOLO"
    )
      .trim()
      .toUpperCase() || "SOLO",
    allowedTypeCodes: codes,
    mevcutAracTipi: String(raw.mevcutAracTipi ?? raw.mevcut_arac_tipi ?? "").trim(),
    otomatik: raw.otomatik !== false && typePolicy !== "manual",
    tripFlexEnabled: raw.tripFlexEnabled !== false,
    maxFlexMinutes: Number.isFinite(Number(raw.maxFlexMinutes))
      ? Number(raw.maxFlexMinutes)
      : 3,
    flexMode: String(raw.flexMode || "AUTO").toUpperCase(),
    basitMaliyet: basit,
    seferMaliyet: Number.isFinite(Number(raw.seferMaliyet)) ? Number(raw.seferMaliyet) : 8,
    aracMaliyet: Number.isFinite(Number(raw.aracMaliyet)) ? Number(raw.aracMaliyet) : 800,
    kiyas_kaynagi:
      String(raw.kiyas_kaynagi || raw.kiyasKaynagi || "baz").trim().toLowerCase() === "son_yayin"
        ? "son_yayin"
        : "baz",
    kiyas_senaryo_id:
      raw.kiyas_senaryo_id != null && String(raw.kiyas_senaryo_id).trim() !== ""
        ? Number(raw.kiyas_senaryo_id) || String(raw.kiyas_senaryo_id)
        : null,
  };
}

export function paramsFromHatRow(row = {}, fallback = {}) {
  return normalizeParams({
    ...fallback,
    ...row,
    typePolicy: row.typePolicy ?? row.type_policy ?? fallback.typePolicy,
    vehicleTypeCode:
      row.vehicleTypeCode ?? row.vehicle_type_code ?? row.aracTipi ?? fallback.vehicleTypeCode,
    allowedTypeCodes: row.allowedTypeCodes ?? row.allowed_type_codes ?? fallback.allowedTypeCodes,
    mevcutAracTipi: row.mevcutAracTipi ?? row.mevcut_arac_tipi ?? fallback.mevcutAracTipi,
  });
}

function valuesEqual(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = asArray(a);
    const bb = asArray(b);
    return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
  }
  if (typeof a === "number" || typeof b === "number") {
    return Number(a) === Number(b);
  }
  if (typeof a === "boolean" || typeof b === "boolean") {
    return Boolean(a) === Boolean(b);
  }
  return String(a ?? "") === String(b ?? "");
}

export function pickOverrides(params, globalParams) {
  const g = normalizeParams(globalParams);
  const p = normalizeParams({ ...g, ...params });
  const out = {};
  for (const key of OVERRIDE_KEYS) {
    if (!valuesEqual(p[key], g[key])) out[key] = p[key];
  }
  return out;
}

export function sanitizeOverrides(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const key of OVERRIDE_KEYS) {
    if (raw[key] !== undefined) out[key] = raw[key];
  }
  return out;
}

export function effectiveParams(globalParams, lineOverrides, hatNo) {
  const g = normalizeParams(globalParams);
  const over = sanitizeOverrides((lineOverrides || {})[String(hatNo ?? "").trim()] || {});
  return normalizeParams({ ...g, ...over });
}

export function setHatOverride(lineOverrides, hatNo, patch) {
  const key = String(hatNo ?? "").trim();
  if (!key) return { ...(lineOverrides || {}) };
  const next = { ...(lineOverrides || {}) };
  const merged = sanitizeOverrides({ ...(next[key] || {}), ...patch });
  if (!Object.keys(merged).length) {
    delete next[key];
    return next;
  }
  next[key] = merged;
  return next;
}

export function clearHatOverride(lineOverrides, hatNo, fields) {
  const key = String(hatNo ?? "").trim();
  const next = { ...(lineOverrides || {}) };
  if (!key || !next[key]) return next;
  if (!fields || fields === true) {
    delete next[key];
    return next;
  }
  const list = Array.isArray(fields) ? fields : [fields];
  const copy = { ...next[key] };
  list.forEach((f) => {
    delete copy[f];
  });
  if (!Object.keys(copy).length) delete next[key];
  else next[key] = copy;
  return next;
}

export function overrideCount(lineOverrides, hatNo) {
  const over = (lineOverrides || {})[String(hatNo ?? "").trim()] || {};
  return Object.keys(over).length;
}

/** Override alanları → ağ koşusu `param` anahtarları (API snake_case). */
export const OVERRIDE_API_KEYS = {
  doluluk: "doluluk",
  kapasite: "kapasite",
  minFrekans: "minFrekans",
  typePolicy: "type_policy",
  vehicleTypeCode: "vehicle_type_code",
  allowedTypeCodes: "allowed_type_codes",
  mevcutAracTipi: "mevcut_arac_tipi",
  otomatik: "otomatik",
  tripFlexEnabled: "tripFlexEnabled",
  maxFlexMinutes: "maxFlexMinutes",
  flexMode: "flexMode",
};

export const OVERRIDE_FIELD_LABELS = {
  doluluk: "Doluluk",
  kapasite: "Kapasite",
  minFrekans: "Min frk",
  typePolicy: "Araç politikası",
  vehicleTypeCode: "Araç",
  allowedTypeCodes: "İzinli tipler",
  mevcutAracTipi: "Mevcut araç tipi",
  otomatik: "Otomatik",
  tripFlexEnabled: "Flex",
  maxFlexMinutes: "Flex dk",
  flexMode: "Flex yöntemi",
};

function formatOverrideValue(key, value) {
  if (key === "doluluk") return `%${value}`;
  if (key === "otomatik" || key === "tripFlexEnabled") return value ? "evet" : "hayır";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (value == null || value === "") return "—";
  return String(value);
}

export function formatOverrideDiffLine(key, globalVal, hatVal) {
  const label = OVERRIDE_FIELD_LABELS[key] || key;
  return `${label}: genel ${formatOverrideValue(key, globalVal)} → bu hat ${formatOverrideValue(key, hatVal)}`;
}

export function overrideDiffLines(globalParams, lineOverrides, hatNo) {
  const over = sanitizeOverrides((lineOverrides || {})[String(hatNo ?? "").trim()] || {});
  const g = normalizeParams(globalParams);
  return Object.keys(over).map((key) => formatOverrideDiffLine(key, g[key], over[key]));
}

/** Detay paneli tek satır: "Bu hat genelden farklı çalıştı: doluluk %90". */
export function overrideShortLine(globalParams, lineOverrides, hatNo) {
  const over = sanitizeOverrides((lineOverrides || {})[String(hatNo ?? "").trim()] || {});
  const keys = Object.keys(over);
  if (!keys.length) return "";
  const parts = keys.map((key) => {
    if (key === "doluluk") return `doluluk %${over[key]}`;
    return `${(OVERRIDE_FIELD_LABELS[key] || key).toLowerCase()} ${formatOverrideValue(key, over[key])}`;
  });
  return `Bu hat genelden farklı çalıştı: ${parts.join(", ")}`;
}

/** Seyrek override → API param (yalnız farklar, type_policy dahil). */
export function lineOverrideToApiParam(diff) {
  if (!diff || typeof diff !== "object") return null;
  const out = {};
  for (const key of OVERRIDE_KEYS) {
    if (diff[key] === undefined) continue;
    const apiKey = OVERRIDE_API_KEYS[key] || key;
    out[apiKey] = diff[key];
  }
  return Object.keys(out).length ? out : null;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value == null) return "";
  if (typeof value === "boolean" || typeof value === "number") return value;
  return String(value);
}

export function paramsFingerprint(params) {
  const p = normalizeParams(params);
  const parts = PARAM_KEYS.map((key) => `${key}=${JSON.stringify(stableValue(p[key]))}`);
  return parts.join("|");
}

/** FNV-1a 32-bit; tarayıcı crypto'suz, deterministik. */
export function hashParams(params) {
  const text = paramsFingerprint(params);
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function rowFieldsFromParams(params) {
  const p = normalizeParams(params);
  return {
    gun: p.gun,
    doluluk: p.doluluk,
    kapasite: p.kapasite,
    minFrekans: p.minFrekans,
    aracTipi: p.vehicleTypeCode,
    typePolicy: p.typePolicy,
    type_policy: p.typePolicy,
    otomatik: p.otomatik,
    vehicleTypeCode: p.vehicleTypeCode,
    vehicle_type_code: p.vehicleTypeCode,
    allowedTypeCodes: [...p.allowedTypeCodes],
    allowed_type_codes: [...p.allowedTypeCodes],
    mevcutAracTipi: p.mevcutAracTipi,
    mevcut_arac_tipi: p.mevcutAracTipi,
    tripFlexEnabled: p.tripFlexEnabled,
    maxFlexMinutes: p.maxFlexMinutes,
    flexMode: p.flexMode,
    seferMaliyet: p.seferMaliyet,
    aracMaliyet: p.aracMaliyet,
  };
}

/**
 * Eski preset/hatRows → globalParams + seyrek farklar.
 * Global taban: extras, yoksa ilk dolu satır.
 */
export function reduceHatRowsToParams(hatRows = [], extras = {}) {
  const rows = (hatRows || []).filter((r) => String(r?.hatNo ?? "").trim());
  const first = rows[0] || {};
  const base = paramsFromHatRow(first, extras);
  const globalParams = normalizeParams({
    ...base,
    gun: extras.gun || base.gun,
    doluluk: extras.doluluk ?? extras.hedefDoluluk ?? base.doluluk,
    basitMaliyet:
      extras.basitMaliyet ??
      rows.some((r) => String(r.type_policy || r.typePolicy || "").toLowerCase() === "classic"),
    seferMaliyet: extras.seferMaliyet ?? base.seferMaliyet,
    aracMaliyet: extras.aracMaliyet ?? base.aracMaliyet,
  });
  const lineOverrides = {};
  rows.forEach((row) => {
    const hatNo = String(row.hatNo).trim();
    const diff = pickOverrides(paramsFromHatRow(row, globalParams), globalParams);
    if (Object.keys(diff).length) lineOverrides[hatNo] = diff;
  });
  return {
    globalParams,
    lineOverrides,
    hatNos: rows.map((r) => String(r.hatNo).trim()),
  };
}

export function expandParamsToRowFields(globalParams, lineOverrides, hatNos) {
  return (hatNos || []).map((hatNo) => ({
    hatNo: String(hatNo).trim(),
    ...rowFieldsFromParams(effectiveParams(globalParams, lineOverrides, hatNo)),
  }));
}

export function serializeDecisionState(state) {
  return {
    version: 1,
    globalParams: normalizeParams(state.globalParams),
    lineOverrides: state.lineOverrides || {},
    selectedScenarioByHat: state.selectedScenarioByHat || {},
    approvedHats: state.approvedHats || {},
    visitedHats: state.visitedHats || {},
    paramSignatureByHat: state.paramSignatureByHat || {},
    hatNotes: state.hatNotes || {},
  };
}

export function parseDecisionState(raw) {
  if (!raw || raw.version !== 1) return null;
  return {
    globalParams: normalizeParams(raw.globalParams),
    lineOverrides: raw.lineOverrides || {},
    selectedScenarioByHat: raw.selectedScenarioByHat || {},
    approvedHats: raw.approvedHats || {},
    visitedHats: raw.visitedHats && typeof raw.visitedHats === "object" ? raw.visitedHats : {},
    paramSignatureByHat: raw.paramSignatureByHat || {},
    hatNotes: raw.hatNotes && typeof raw.hatNotes === "object" ? raw.hatNotes : {},
  };
}
